import { useCallback, useEffect, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import dayjs, { Dayjs } from "dayjs";
import { reporteMovimientosGridColumnDefinitions, ReporteResumenCajaModel, transformReporteDeMovimientosData } from "../models/ReporteMovimientosModel";
import { ReporteCajasModel } from "../models/ReporteCajasModel";
import DateRangePicker from "./components/DateRangePickerComponent";
import LocalSelector from "./components/LocalSelector";
import { fetchGetCajas, fetchGetMovimientos } from "../services/reporteService";
import {
    CircularProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    Button,
    Box,
    Grid,
    Typography,
} from "@mui/material";
import DynamicGrid from "../components/DynamicTable";


const calculateSumByFormaPago = (data: ReporteResumenCajaModel["movimientos"]) => {
  return data.reduce((acc, item) => {
    const key = item.formaPago;
    const total = (item.debe  || 0) - (item.haber || 0);
    acc[key] = (acc[key] || 0) + total;
    return acc;
  }, {} as Record<string, number>);
};

const calculateSumByCuenta = (data: ReporteResumenCajaModel["movimientos"]) => {
  return data.reduce((acc, item) => {
    const key = item.cuenta;
    const total = (item.debe  || 0) - (item.haber || 0);
    acc[key] = (acc[key] || 0) + total;
    return acc;
  }, {} as Record<string, number>);
};

const ReporteCajas = () => {
    const { getUser } = useUserContext();
    const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
    const [loadingCajas, setLoadingCajas] = useState(false);
    const [loadingReporte, setLoadingReporte] = useState(false);
    const [reporteCajas, setReporteCajas] = useState<ReporteCajasModel[]>([]);
    const [selectedCaja, setSelectedCaja] = useState<string>("");
    const [reporteMovimientos, setReporteMovimientos] = useState<ReporteResumenCajaModel>();
    const [selectedRange, setSelectedRange] = useState<{ startDate: Dayjs | null; endDate: Dayjs | null }>({
        startDate: null,
        endDate: null,
    });

    const handleDateChange = (range: { startDate: Dayjs | null; endDate: Dayjs | null }) => {
        setSelectedRange(range);
    };

    const handleLocalChange = (idLocal: string | undefined) => {
        setIdLocal(idLocal);
    };

    const handleCajaChange = (event: SelectChangeEvent) => {
        const pvandidcaja = event.target.value;
        if (pvandidcaja) {
            const pvIdCaja = pvandidcaja.split("|");
            if (pvIdCaja.length === 3) {
                setSelectedCaja(pvandidcaja);
            }
        }
    };

    const handleObtenerMovimientos = () => {
        const pvIdCaja = selectedCaja.split("|");
        const pv = pvIdCaja[0];
        const idCaja = pvIdCaja[1];
        const cajaAbierta = pvIdCaja[2] === "1";
        getReporteMovimientos(idCaja, pv, cajaAbierta);
    };

    const getReporteCajas = useCallback(() => {
        const user = getUser();
        if (user && idLocal && selectedRange.startDate && selectedRange.endDate) {
            setLoadingCajas(true);
            fetchGetCajas(user!, idLocal!, selectedRange.startDate!, selectedRange.endDate!)
                .then((res: ReporteCajasModel[]) => {
                    setReporteCajas(res);
                    setLoadingCajas(false);
                })
                .catch((err) => {
                    setLoadingCajas(false);
                });
        } else {
            setReporteCajas([]);
            setLoadingCajas(false);
        }
    }, [getUser, idLocal, selectedRange.startDate, selectedRange.endDate]);

    useEffect(() => {
        getReporteCajas();
    }, [getReporteCajas])

    const getReporteMovimientos = (idCaja: string, pv: string, cajaAbierta: boolean) => {
        const user = getUser();
        if (user && idLocal && idCaja && pv) {
            setLoadingReporte(true);
            fetchGetMovimientos(getUser()!, idLocal!, idCaja, pv, cajaAbierta)
                .then((res: ReporteResumenCajaModel) => {
                    const movs = transformReporteDeMovimientosData(res.movimientos);
                    setReporteMovimientos({idCaja: res.idCaja, idLocal: res.idLocal, pv: res.pv, movimientos: movs});
                    setLoadingReporte(false);
                })
                .catch((err) => {
                    setLoadingReporte(false);
                });
        }
    };

    const sumByFormaPago = reporteMovimientos?.movimientos
    ? calculateSumByFormaPago(reporteMovimientos.movimientos)
    : {};

    const sumByCuenta = reporteMovimientos?.movimientos
    ? calculateSumByCuenta(reporteMovimientos.movimientos)
    : {};

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Reporte de Cajas
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <DateRangePicker onDateChange={handleDateChange} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <LocalSelector onLocalChange={handleLocalChange} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {loadingCajas ? (
                        <Box sx={{ textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Select
                            fullWidth
                            value={selectedCaja}
                            onChange={handleCajaChange}
                            displayEmpty
                            sx={{ height: "100%" }}
                        >
                            <MenuItem value="" disabled>
                                Seleccione una Caja
                            </MenuItem>
                            {reporteCajas.map((l) => (
                                <MenuItem key={l.idCaja} value={`${l.pv}|${l.idCaja}|${l.activo}`}>
                                    {dayjs(l.fecha).format('DD-MM-YYYY')}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Grid>
            </Grid>
            {!loadingCajas && reporteCajas.length > 0 && (
                <Box sx={{ textAlign: "right", mb: 3 }}>
                    <Button
                        onClick={handleObtenerMovimientos}
                        variant="contained"
                        color="primary"
                        disabled={loadingReporte}
                        startIcon={loadingReporte ? <CircularProgress color="inherit" size={18} /> : undefined}
                    >
                        {loadingReporte ? "Obteniendo..." : "Obtener Movimientos"}
                    </Button>
                </Box>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                {Object.keys(sumByFormaPago).length > 0 && (
                  <Box>
                    <Typography variant="h6">Totales por Forma de Pago:</Typography>
                    {Object.entries(sumByFormaPago).map(([key, value]) => (
                      <Typography key={key}>
                        {key}: {value.toLocaleString("es-AR", { style: "currency", currency: "ARS" })}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {Object.keys(sumByCuenta).length > 0 && (
                  <Box>
                    <Typography variant="h6">Totales por Cuenta:</Typography>
                    {Object.entries(sumByCuenta).map(([key, value]) => (
                      <Typography key={key}>
                        {key}: {value.toLocaleString("es-AR", { style: "currency", currency: "ARS" })}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
            <DynamicGrid
              data={reporteMovimientos?.movimientos || []}
              customHeaderMap={reporteMovimientosGridColumnDefinitions}
              pageSize={10}
              removeIfNotCustom={true}
            />
        </Box>
    );
};

export default ReporteCajas;
